import type {
  BasicDigitalTwin,
  BasicDigitalTwinComponent,
  DtdlProperty,
  DtdlSchema,
  DtdlMapSchema,
  DtdlObjectSchema,
  DtdlEnumSchema,
  DtdlComponent
} from 'src/models'
import { extend, is } from 'quasar'
import type { AddOperation, Operation } from 'fast-json-patch'
import { applyPatch, compare } from 'fast-json-patch'

import { useModelsStore, useAdminModelsStore } from 'src/stores/models'
import type { DtdlComplexSchema } from 'src/models/Dtdl'
import { objectIsEmpty } from 'src/utils/objects'
import { isObject } from 'src/utils/type-checks'

/**
 * cleanTwin, edits the given twin
 * @param {BasicDigitalTwin} twin
 */
export const cleanTwin = (twin: BasicDigitalTwin): BasicDigitalTwin => {
  // remove empty properties, empty objects or objects that only have null values
  for (const key of Object.keys(twin).filter(
    (x) => !x?.includes('$') || x?.includes('$metadata')
  )) {
    if (
      twin[key] === null ||
      twin[key] === undefined ||
      objectIsEmpty(twin[key]) ||
      stringIsNullOrEmpty(twin[key])
    ) {
      delete twin[key]
    } else {
      // recursively also clean subobjects
      removeNullValues(twin[key])
    }
  }
  return twin
}

const stringIsNullOrEmpty = (prop: unknown) => {
  return (
    (typeof prop === 'string' || prop instanceof String) && (!prop || String(prop).length === 0)
  )
}

const removeNullValues = (prop: unknown) => {
  if (typeof prop === 'object' && prop !== null && prop !== undefined) {
    for (const [key, value] of Object.entries(prop)) {
      if (
        value === null ||
        value === undefined ||
        objectIsEmpty(value) ||
        stringIsNullOrEmpty(value)
      ) {
        delete (prop as Record<string, unknown>)[key]
      } else {
        removeNullValues((prop as Record<string, unknown>)[key])
      }
    }
  }
}

/**
 * copyTwin, copy the twin and remove not writable props
 * @param {BasicDigitalTwin} twin
 * @param {DtdlProperty[]} properties
 */
export const copyTwin = <T extends BasicDigitalTwin | BasicDigitalTwinComponent>(
  twin: T,
  properties: DtdlProperty[] | undefined
): T => {
  const _twin = extend<BasicDigitalTwin | BasicDigitalTwinComponent>(true, {}, twin)
  delete _twin.$dtId
  if (properties && _twin) {
    const propsToDelete = properties?.filter((p) => !p.writable)
    for (const prop of propsToDelete) {
      if (_twin[prop.name]) delete _twin[prop.name]
    }
  }
  return _twin as T
}

const _getTypeNested = (
  path: string,
  schema: DtdlSchema,
  values: (string | number)[],
  props: { path: string; schema: string; values: unknown[] }[]
) => {
  if (isObject(schema)) {
    if (schema['@type'] === 'Map') {
      _getTypeNested(
        String(path + '.{' + (schema as DtdlMapSchema).mapKey.schema + '}'),
        (schema as DtdlMapSchema).mapValue.schema,
        values,
        props
      )
    }
    if (schema['@type'] === 'Object') {
      for (const field of (schema as DtdlObjectSchema).fields) {
        _getTypeNested(String(path + '.' + field.name), field.schema, values, props)
      }
    }
    if (schema['@type'] === 'Enum') {
      _getTypeNested(
        String(path),
        (schema as DtdlEnumSchema).valueSchema,
        (schema as DtdlEnumSchema).enumValues.map((ev) => ev.enumValue),
        props
      )
    }
  } else {
    props.push({ path, schema: String(schema), values })
  }
  return props
}

const _nestedProps = (
  key: string,
  value: unknown,
  validations: { path: string; schema: string; values: unknown[] }[],
  invalid: string[]
) => {
  if (key?.startsWith('$')) return
  if (!validations || validations.length === 0) return
  if (isObject(value)) {
    for (const [_key, _value] of Object.entries(value)) {
      const _validations = extend<{ path: string; schema: string; values: unknown[] }[]>(
        true,
        [],
        validations
      )
      const _path = key + '.' + _key
      const _pathSplit = _path!.split('.')
      for (const _validation of _validations) {
        const _validationSplit = _validation.path.split('.')
        const _validationSplitIndex = _validationSplit.indexOf('{string}')
        if (_validationSplitIndex > -1 && _pathSplit.length > _validationSplitIndex) {
          _validationSplit[_validationSplitIndex] = _pathSplit[_validationSplitIndex]
          _validation.path = _validationSplit.join('.')
        }
      }
      _nestedProps(
        _path,
        _value,
        _validations.filter((p) => p.path.startsWith(_path)),
        invalid
      )
    }
  } else if (validations.some((p) => p.path.startsWith(key))) {
    const _validation = validations.find((p) => p.path.startsWith(key))
    if (_validation && _validation.values.length > 0 && !_validation.values.includes(value)) {
      invalid.push(key)
    }
  } else {
    invalid.push(key)
  }
}

/**
 * validateTwin, validate the twin
 * 1. check if value has right type
 * 2. check if value is an enum with the right value
 * 3. check if value is still present in a nested prop
 * 4. ...
 * @param {BasicDigitalTwin} twin
 * @param {DtdlProperty[]} properties
 */
const _validateTwin = (
  twin: BasicDigitalTwin | BasicDigitalTwinComponent,
  properties: DtdlProperty[] | undefined
): string[] => {
  const _twin = extend<BasicDigitalTwin | BasicDigitalTwinComponent>(true, {}, twin)
  const invalid: string[] = []
  if (properties && _twin) {
    const validations: { path: string; schema: string; values: unknown[] }[] = []
    for (const prop of properties) {
      validations.push(..._getTypeNested(prop.name, prop.schema, [], []))
    }
    for (const [key, value] of Object.entries(_twin)) {
      _nestedProps(key, value, validations, invalid)
    }
  }
  return invalid
}

/**
 * twinToPatch, transform a twin to array of operations
 * @param {BasicDigitalTwin} twin
 * @param {string} op
 * @param {boolean} nestedSeparately
 * nestedSeparately
 * - if true a property object will have separate patch op's (IMPORTANT!! if the prop exists it will append, otherwise it will fail)
 * - if false a property object will have one patch op (IMPORTANT!! if the prop exists it will overwrite)
 * @returns {Operation[]}
 */
const _twinToPatch = (
  twin: BasicDigitalTwin,
  op: 'add' = 'add',
  nestedSeparately: boolean
): Operation[] => {
  const adminModelsStore = useAdminModelsStore()
  const modelsStore = useModelsStore()
  // Other operations not yet supported
  if (!op || op !== 'add') return []
  const _makePatchable = (operation: AddOperation<unknown>, operations: Operation[]) => {
    // Deal with nested objects, this avoids overwriting entire objects, when only parts are defined
    // Downside is that it will fail in case the object doesn't exist yet

    // eslint-disable-next-line sonarjs/no-selector-parameter
    if (nestedSeparately && is.object(operation.value)) {
      for (const [_k, _v] of Object.entries(operation.value)) {
        const _operation = extend<AddOperation<unknown>>(true, {}, operation)
        _operation.path = operation.path + '/' + _k
        _operation.value = _v
        _makePatchable(_operation, operations)
      }
      // TODO: Support array properties for DTDLv3
      // } else if (_valueIsArray(operation)) {
      //   for (const [_k, _v] of Object.entries(operation.value)) {
      //     const _operation = extend<Operation>(true, {}, operation)
      //     _operation.path = operation.path + '[' + _k + ']'
      //     _operation.value = _v
      //     _makePatchable(_operation, operations)
      //   }
    } else {
      // only check if a property expects an object and the operation value is a string
      // only supported for first level properties
      if (operation?.path?.split('/').length === 2) {
        const propertyName = operation?.path?.split('/')[1]
        const property =
          modelsStore.propertyByName(twin?.$metadata?.$model, propertyName) ||
          adminModelsStore.propertyByName(twin?.$metadata?.$model, propertyName)
        const schema = property?.schema
        if (
          property &&
          schema &&
          typeof operation.value === 'string' &&
          typeof schema === 'object'
        ) {
          try {
            const _value = JSON.parse(operation.value)
            operation.value = _value
          } catch {
            /* empty */
          }
        }
      }
      operations.push(operation)
    }
  }
  const _operations: Operation[] = []
  const _tempOperations: AddOperation<unknown>[] = []
  _tempOperations.push(
    ...compare({}, twin).filter(
      (x): x is AddOperation<unknown> =>
        x.op === 'add' && (!x.path.includes('$') || x.path.includes('$metadata'))
    )
  )
  for (const _operation of _tempOperations) {
    _makePatchable(_operation, _operations)
  }
  return _operations
}

/**
 * Transform a twin to array of patch operations, expanding nested object properties
 * into separate patch operations (append semantics — fails if object doesn't exist yet).
 */
export const twinToPatch = (twin: BasicDigitalTwin, op: 'add' = 'add'): Operation[] =>
  _twinToPatch(twin, op, true)

/**
 * Transform a twin to array of patch operations, keeping nested object properties
 * as a single patch operation (overwrite semantics).
 */
export const twinToPatchFlat = (twin: BasicDigitalTwin, op: 'add' = 'add'): Operation[] =>
  _twinToPatch(twin, op, false)

/**
 * Converts a JSON Patch document to a partial BasicDigitalTwin by applying the patch to an empty BasicDigitalTwin object
 * @param twinId The twin ID
 * @param patch The patch document (array of operations), which should have at least /$metadata/$model to define a valid model. It should also only have 'add' operations.
 * @returns The new (partial) Digital Twin
 */
export const patchToTwin = (twinId: string, patch: Operation[], modelId?: string) => {
  const twin: BasicDigitalTwin = {
    $dtId: twinId,
    $metadata: { $model: modelId || '' }, // placeholder, patch should include model
    // add empty objects for nested patches (applying the patch would otherwise fail)
    ...patch.reduce<{ [key: string]: object }>((obj, p) => {
      const pathsSplit = p?.path?.split('/')
      // remove first element
      pathsSplit?.shift()
      // assign to parts
      const parts = pathsSplit || []
      let parent: Record<string, unknown> = obj
      if (parts.length > 1) {
        for (let i = 0; i < parts.length - 1; i++) {
          const part = parts[i]
          if (i === parts.length - 2 && !parent[part]) parent[part] = {}
          else {
            if (!parent[part]) parent[part] = {}
            parent = parent[part] as Record<string, unknown>
          }
        }
      }
      return obj
    }, {})
  }
  const { newDocument } = applyPatch(twin, patch)
  return newDocument
}

export const addInvalidProperties = (
  twin: BasicDigitalTwin,
  invalidPropertyList: { invalid: boolean; path: string }[],
  propertyList: (DtdlProperty | DtdlComponent)[]
) => {
  const modelsStore = useModelsStore()
  cleanTwin(twin)

  interface SimpleProp {
    name: string
    ['@type']?: string | string[]
    schema?: DtdlSchema
    fields?: DtdlObjectSchema['fields']
  }

  // add invalid properties that are present in object, but not found in model
  const checkRecursive = (
    parentPath: string,
    obj: Record<string, unknown>,
    propList: SimpleProp[]
  ) => {
    const invalidKeys = Object.keys(obj).filter(
      (key) => !key?.startsWith('$') && !propList.some((p) => p.name === key)
    )
    // Check for invalid keys in this level
    for (const path of invalidKeys) {
      invalidPropertyList.push({
        invalid: true,
        path: `${parentPath}/${path}`
      })
    }
    // Check whether we can do this for nested objects and components as well
    const existingObjectProperties = propList.filter(
      (p) => (p.schema as DtdlComplexSchema)['@type'] === 'Object' && obj[p.name]
    )
    for (const objProp of existingObjectProperties) {
      checkRecursive(
        `${parentPath}/${objProp.name}`,
        obj[objProp.name] as Record<string, unknown>,
        (objProp.schema as DtdlObjectSchema).fields
      )
    }
    const components = propList.filter((p) => p['@type'] === 'Component' && obj[p.name])
    for (const comp of components) {
      const compProps = modelsStore.properties(comp.schema as string)
      checkRecursive(
        `${parentPath}/${comp.name}`,
        obj[comp.name] as Record<string, unknown>,
        compProps as SimpleProp[]
      )
    }
  }
  checkRecursive('', twin, propertyList as SimpleProp[])
}

export const escapeCharsTwinId = (str: string) => {
  return (str ?? '').replace(/'/g, "\\'")
}
